// Document Upload History Component
// Displays all documents uploaded and processed for a pursuit

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Loader2,
  RefreshCw,
  FileStack,
  Sparkles,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status config for visual indicators
const STATUS_CONFIG = {
  COMPLETED: {
    icon: CheckCircle2,
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    label: 'AI Shredded'
  },
  PROCESSING: {
    icon: Loader2,
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    label: 'Processing',
    animate: true
  },
  PENDING: {
    icon: Clock,
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    label: 'Pending'
  },
  FAILED: {
    icon: AlertCircle,
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    label: 'Failed'
  }
};

function DocumentRow({ document, isExpanded, onToggle, onDelete }) {
  const config = STATUS_CONFIG[document.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = config.icon;
  const [isDeleting, setIsDeleting] = useState(false);
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!confirm('Delete this failed upload from history?')) return;
    
    setIsDeleting(true);
    try {
      await onDelete(document.id);
    } finally {
      setIsDeleting(false);
    }
  };
  
  return (
    <div 
      className={`border rounded-lg overflow-hidden transition-all ${config.border} ${isExpanded ? 'shadow-md' : ''}`}
      data-testid={`doc-row-${document.id}`}
    >
      {/* Main Row */}
      <div 
        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-slate-50 ${config.bg}`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
            <StatusIcon className={`w-4 h-4 ${config.color} ${config.animate ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <p className="font-medium text-slate-800 text-sm truncate max-w-[200px] md:max-w-[300px]">
              {document.file_name}
            </p>
            <p className="text-xs text-slate-500">
              {formatDate(document.upload_date)}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge className={`${config.bg} ${config.color} border ${config.border} text-xs`}>
            {config.label}
          </Badge>
          {document.confidence_score && (
            <Badge variant="outline" className="text-xs">
              {Math.round(document.confidence_score)}% conf
            </Badge>
          )}
          {document.status === 'FAILED' && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={handleDelete}
              disabled={isDeleting}
              title="Delete failed upload"
            >
              {isDeleting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
            </Button>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 py-3 bg-white border-t border-slate-100">
          {document.summary ? (
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">AI Summary</p>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {document.summary}
                </p>
              </div>
            </div>
          ) : document.content_preview ? (
            <div className="flex items-start gap-2">
              <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Content Preview</p>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">
                  {document.content_preview}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-slate-500">
                  No summary available yet. Process this document in the Document Ingestion Hub to extract content.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Error message for failed extractions */}
      {isExpanded && document.status === 'FAILED' && document.status_message && (
        <div className="px-4 py-3 bg-red-50 border-t border-red-100">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-red-600 mb-1">Error Details</p>
              <p className="text-sm text-red-700">
                {document.status_message}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function DocumentHistory({ pursuitId, authToken }) {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const fetchDocuments = async () => {
    if (!pursuitId || !authToken) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/documents`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }
      
      const data = await response.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Error fetching document history:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchDocuments();
  }, [pursuitId, authToken]);
  
  const handleToggleExpand = (docId) => {
    setExpandedId(expandedId === docId ? null : docId);
  };

  const handleDeleteFailed = async (jobId) => {
    try {
      const response = await fetch(`${API_URL}/api/ai/extraction-jobs/${jobId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete');
      }
      
      toast.success('Failed upload removed from history');
      // Remove from local state
      setDocuments(docs => docs.filter(d => d.id !== jobId));
    } catch (err) {
      console.error('Error deleting failed job:', err);
      toast.error(err.message || 'Failed to delete');
    }
  };
  
  // Empty state
  if (!isLoading && documents.length === 0 && !error) {
    return (
      <Card className="border-2 border-dashed border-slate-200" data-testid="document-history-empty">
        <CardContent className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-3">
              <FileStack className="w-7 h-7 text-slate-400" />
            </div>
            <h3 className="font-semibold text-slate-700 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No Documents Yet
            </h3>
            <p className="text-sm text-slate-500 max-w-xs">
              Upload an RFP document using the "AI Shredder" button above to extract opportunity data automatically.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-slate-200 bg-white shadow-sm" data-testid="document-history">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle
            className="text-lg font-semibold flex items-center gap-2 cursor-pointer select-none"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            onClick={() => setCollapsed(v => !v)}
            data-testid="doc-history-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileStack className="w-4 h-4 text-purple-600" />
            </div>
            Document Upload History
            {documents.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {documents.length} file{documents.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchDocuments}
            disabled={isLoading}
            data-testid="refresh-documents-btn"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>

      {!collapsed && (
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-500 text-sm">Loading documents...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 p-4 bg-red-50 rounded-lg border border-red-200">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <div>
              <p className="text-sm font-medium text-red-700">Failed to load documents</p>
              <p className="text-xs text-red-600">{error}</p>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="ml-auto"
              onClick={fetchDocuments}
            >
              Retry
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <DocumentRow
                key={doc.id}
                document={doc}
                isExpanded={expandedId === doc.id}
                onToggle={() => handleToggleExpand(doc.id)}
                onDelete={handleDeleteFailed}
              />
            ))}
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}

export default DocumentHistory;
